// no-dd-sa:javascript-best-practices/no-cond-assign,javascript-code-style/strict-equals,javascript-code-style/assignment-name
import React, { useEffect } from 'react';
import { Box, Container, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, Checkbox, TableHead, TableSortLabel } from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { healthBenefitState } from '../../atoms/HealthBenefitState';
import Pagination from '../../components/Pagination';
import ShowMore from '../../components/ShowMore';
import { enableDisableAllHBR, enableDisableHBR, listHealthBenefits } from '../../repositories/HttpRepository';
import AlertDialog from './dialog';
import SearchForm from './searchForm';
import useStyles from './styles';

const fields = [
  { id: 'driver_id', numeric: false, disablePadding: true, label: 'Driver ID' },
  {
    id: 'driver_name',
    numeric: false,
    disablePadding: false,
    label: 'Driver Name',
    isDisableSort: true,
  },
  {
    id: 'remark',
    numeric: false,
    disablePadding: false,
    label: 'Remark',
    isDisableSort: true,
  },
  {
    id: 'payout_amount',
    numeric: false,
    disablePadding: false,
    label: 'Payout Amount',
  },
  { id: 'status', numeric: false, disablePadding: false, label: 'Status' },
  { id: 'enabled', numeric: false, disablePadding: false, label: 'Enabled', isDisableSort: true },
  {
    id: 'activated_ts',
    numeric: false,
    disablePadding: false,
    label: 'Days Left',
    isDisableSort: false,
  },
  {
    id: 'activated_ts',
    numeric: false,
    disablePadding: false,
    label: 'Activated Date',
    isDisableSort: true,
  },
  {
    id: 'avg_engaged_time_per_week',
    numeric: false,
    disablePadding: false,
    label: 'Avg Engaged Time Per Week (Hr)',
    isDisableSort: true,
  },
  {
    id: 'earning_per_quarter',
    numeric: false,
    disablePadding: false,
    label: 'Earnings Per Quarter',
    isDisableSort: true,
  },
  { id: '', numeric: false, disablePadding: false, label: '' },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, numSelected, rowCount, headCells, selectAll, orderBy, desc, onRequestSort } = props;
  const tmpHeadCells = [...headCells];
  tmpHeadCells.pop();

  const createSortHandler = (property) => () => {
    onRequestSort(property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={(selectAll == false && rowCount > 0 && numSelected === rowCount) || (selectAll == true && numSelected < rowCount)}
            onChange={onSelectAllClick}
            color="primary"
            size="small"
            slotProps={{
              input: { 'aria-label': 'select all desserts' },
            }}
          />
        </TableCell>
        {tmpHeadCells.map((headCell, index) => (
          <TableCell
            key={`${headCell.id}-${index}`}
            align={headCell.align ? headCell.align : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? (desc ? 'desc' : 'asc') : false}
          >
            {!headCell.isDisableSort && headCell.id ? (
              <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? (desc ? 'desc' : 'asc') : 'asc'} onClick={createSortHandler(headCell.id)}>
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  headCells: PropTypes.array.isRequired,
  selectAll: PropTypes.bool,
  orderBy: PropTypes.string,
  desc: PropTypes.bool,
  onRequestSort: PropTypes.func.isRequired,
};

const HealthBenefitList = () => {
  const [healthBenefits, setHealthBenefits] = useRecoilState(healthBenefitState);

  const {
    items,
    page,
    size,
    desc,
    order_by,
    total,
    sum_payout_amount,
    isListing,
    search_text,
    status,
    is_dsp_driver,
    years,
    quarters,
    enabled,
    activated,
    total_driving_time,
    total_driving_distance,
  } = healthBenefits;

  const { classes } = useStyles();
  const [selected, setSelected] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [enableSelectedIds, setEnableSelectedIds] = React.useState([]);
  const [disableSelectedIds, setDisableSelectedIds] = React.useState([]);

  const getList = (params) => {
    let queryParams = {
      size,
      page,
      search_text,
      status,
      is_dsp_driver,
      order_by,
      desc,
      years,
      quarters,
      enabled,
      activated,
    };
    if (params) queryParams = { ...queryParams, ...params };

    // Don't fetch if years or quarters are not selected
    if (!years || years.length === 0 || !quarters || quarters.length === 0) {
      return;
    }

    // Set loading state
    setHealthBenefits((prev) => ({ ...prev, isListing: true }));

    listHealthBenefits(queryParams)
      .then((resp) => {
        if (resp.ok) {
          const data = resp.data;

          setHealthBenefits((prev) => ({
            ...prev,
            items: data.items,
            page: data.page,
            size: data.size,
            total: data.total,
            sum_payout_amount: data.sum_payout_amount,
            total_driving_time: data.total_driving_time,
            total_driving_distance: data.total_driving_distance,
            isListing: false,
          }));
        }
      })
      .catch((error) => {
        // console.error('Error fetching health benefits:', error);
        setHealthBenefits((prev) => ({ ...prev, isListing: false }));
      });
  };

  useEffect(() => {
    resetConditions();
    getList();
  }, [search_text, status, is_dsp_driver, order_by, desc, years, quarters, enabled, activated]);

  useEffect(() => {
    getList();
  }, [page]);

  const handleChangePage = (event, newPage) => {
    setHealthBenefits({ ...healthBenefits, page: newPage });
  };

  const handleSelectAllClick = (event) => {
    if (items.length == 0) {
      return false;
    }

    setSelected([]);
    if (event.target.checked) {
      setSelectAll(true);
      return;
    }
    setSelectAll(false);
  };

  Array.prototype.remove = () => {
    let what,
      a = arguments,
      L = a.length,
      ax;
    while (L && this.length) {
      what = a[--L];
      while ((ax = this.indexOf(what)) !== -1) {
        this.splice(ax, 1);
      }
    }
    return this;
  };

  const handleClick = (event, id, enabled) => {
    const selectedIndex = selected.indexOf(id);

    let newSelected = [];

    if (selectedIndex === -1) {
      //new id
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);

    if (selectedIndex === -1) {
      if (enabled == true) {
        setEnableSelectedIds(enableSelectedIds.concat(id));
      } else {
        setDisableSelectedIds(disableSelectedIds.concat(id));
      }
    } else if (enabled == true) {
      setEnableSelectedIds(enableSelectedIds.remove(id));
    } else {
      setDisableSelectedIds(disableSelectedIds.remove(id));
    }
  };

  const requestEnableDisableAllHBR = (params, isEnable) => {
    let msg = '';
    if (isEnable) {
      msg = 'All selected health benefit requests were enabled successfully!';
    } else {
      msg = 'All selected health benefit requests were disabled successfully!';
    }
    enableDisableAllHBR(params).then((res) => {
      if (res.ok) {
        toast.success(msg);
        // refresh
        getList();
      } else {
        toast.error(res.data?.message || `Failed to ${isEnable ? 'enable' : 'disable'} all selected health benefit requests!`);
      }
    });
  };

  const requestEnableDisableHBR = (params, isEnable) => {
    let msg = '';
    if (isEnable) {
      msg = `${selected.length} health benefit ${selected.length === 1 ? 'request was' : 'requests were'} enabled successfully!`;
    } else {
      msg = `${selected.length} health benefit ${selected.length === 1 ? 'request was' : 'requests were'} disabled successfully!`;
    }
    enableDisableHBR(params).then((res) => {
      if (res.ok) {
        toast.success(msg);
        // refresh
        getList();
      } else {
        toast.error(res.data?.message || `Failed to ${isEnable ? 'enable' : 'disable'} ${selected.length} selected health benefit ${selected.length === 1 ? 'request' : 'requests'}!`);
      }
    });
  };

  Array.prototype.unique = () => {
    let a = this.concat();
    for (let i = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if (a[i] === a[j]) a.splice(j--, 1);
      }
    }

    return a;
  };

  const processRecords = (ids, enable) => {
    const arr = ids.map((id) => {
      return {
        id: id,
        enabled: enable,
      };
    });

    return { health_benefits: arr };
  };

  const resetConditions = () => {
    setSelected([]);
    setSelectAll(false);
    setEnableSelectedIds([]);
    setDisableSelectedIds([]);
  };

  const handleEnable = () => {
    if (selectAll == true) {
      let queryParams = {
        status,
        enabled,
        activated,
        is_dsp_driver,
        year: years[0],
        quarter: quarters[0],
        search_text,
      };
      let exclude_ids = selected
        ? selected.map((item) => {
            return { id: item };
          })
        : [];
      queryParams = {
        ...queryParams,
        exclude_health_benefits: exclude_ids,
        new_enabled: true,
      };

      requestEnableDisableAllHBR(queryParams, true);
    } else {
      const params = processRecords(selected, true);
      requestEnableDisableHBR(params, true);
    }

    //reset
    resetConditions();
  };

  const handleDisable = () => {
    if (selectAll == true) {
      let queryParams = {
        status,
        enabled,
        activated,
        is_dsp_driver,
        year: years[0],
        quarter: quarters[0],
        search_text,
      };
      let exclude_ids = selected
        ? selected.map((item) => {
            return { id: item };
          })
        : [];
      queryParams = {
        ...queryParams,
        exclude_health_benefits: exclude_ids,
        new_enabled: false,
      };

      requestEnableDisableAllHBR(queryParams, false);
    } else {
      const params = processRecords(selected, false);
      requestEnableDisableHBR(params, false);
    }
    //reset
    resetConditions();
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleRequestSort = (property) => {
    const isDesc = order_by === property && desc;
    setHealthBenefits({
      ...healthBenefits,
      order_by: property,
      desc: !isDesc,
      page: 1,
    });
  };

  const totalSelectedNumber = selectAll == true ? total : selected.length;

  let formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const totalSelectedStr = selectAll ? 'all selected' : totalSelectedNumber;
  return (
    <Container maxWidth={false} className={classes.root}>
      <Typography variant="h3" component="h3" style={{ marginBottom: '20px', fontSize: '1.5rem' }}>
        <Box component="div" sx={{ display: 'inline' }}>
          Total health benefit: <span style={{ display: 'inline-block', width: '300px' }}>{total}</span>
        </Box>
        <Box component="div" sx={{ display: 'inline' }} style={{ paddingLeft: '30px' }}>
          Total payout amount&nbsp;: <span>{formatter.format(sum_payout_amount)}</span>
        </Box>
      </Typography>
      <Typography variant="h3" component="h3" style={{ marginBottom: '20px', fontSize: '1.5rem' }}>
        <Box component="div" sx={{ display: 'inline' }}>
          Total driving time&nbsp;&nbsp;&nbsp;:{' '}
          <span style={{ display: 'inline-block', width: '300px' }}>
            {total_driving_time
              ? parseFloat(total_driving_time).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })
              : 0}{' '}
            hours
          </span>
        </Box>
        <Box component="div" sx={{ display: 'inline' }} style={{ paddingLeft: '30px' }}>
          Total driving distance: {total_driving_distance ? parseFloat(total_driving_distance).toLocaleString() : 0} m
        </Box>
      </Typography>
      <SearchForm initValue={search_text} placeholder="Search for driver ID, driver name" />
      <div className={classes.btnDisableEnable}>
        <AlertDialog
          btnString="Enable"
          title={`Do you want to enable ${totalSelectedStr} health benefits ?`}
          description=""
          handleAgree={handleEnable}
          sendMessage={false}
          disabled={(selectAll == false && selected.length == 0) | (selectAll == false && disableSelectedIds.length == 0) | (selectAll == true && selected.length == total) ? true : false}
        />
        <AlertDialog
          btnString="Disable"
          title={`Do you want to disable ${totalSelectedStr} health benefits?`}
          description=""
          handleAgree={handleDisable}
          sendMessage={false}
          disabled={(selectAll == false && selected.length == 0) | (selectAll == false && enableSelectedIds.length == 0) | (selectAll == true && selected.length == total) ? true : false}
        />

        <p style={{ color: '#f44336' }}>* Health benefit is activated once it is enabled the first time. Then, drivers will be able to upload payment request. </p>
      </div>
      <Paper className={classes.paper}>
        <TableContainer>
          <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
            <EnhancedTableHead
              numSelected={selected.length}
              onSelectAllClick={handleSelectAllClick}
              rowCount={total || 0}
              headCells={fields}
              selectAll={selectAll}
              orderBy={order_by}
              desc={desc}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {items &&
                items.map((item, index) => {
                  const labelId = `dsp-table-item-${index}`;
                  const iselectedAll = isSelected(item.id);
                  const isItemSelected = (selectAll == true && !isSelected(item.id)) || (selectAll == false && isSelected(item.id));

                  return (
                    <TableRow hover onClick={(event) => handleClick(event, item.id, item.enabled)} role="checkbox" aria-checked={iselectedAll} tabIndex={-1} key={item.id} selected={iselectedAll}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          size="small"
                          color="primary"
                          slotProps={{
                            input: { 'aria-labelledby': labelId },
                          }}
                        />
                      </TableCell>
                      <TableCell id={labelId}>{item.driver_id}</TableCell>
                      <TableCell>{item.driver_name}</TableCell>
                      <TableCell>
                        <ShowMore text={item.remark} length={30} />
                      </TableCell>
                      <TableCell>{'$' + item.payout_amount}</TableCell>
                      <TableCell>{item.status}</TableCell>
                      <TableCell>{item.enabled == true ? 'Enabled' : 'Disabled'}</TableCell>
                      <TableCell>{item.left_day_before_expired}</TableCell>
                      <TableCell>{item.activated_ts ? moment.utc(item.activated_ts).local().format('YYYY-MM-DD HH:mm') : ''}</TableCell>
                      <TableCell>{Number.parseFloat(item.avg_engaged_time_per_week).toFixed(2)}</TableCell>
                      <TableCell>{formatter.format(item.earning_per_quarter)}</TableCell>
                    </TableRow>
                  );
                })}
              {items.length == 0 && (
                <TableRow>
                  <TableCell colSpan={11} style={{ textAlign: 'center', backgroundColor: '#fff' }}>
                    Record not found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {isListing && <LinearProgress />}
        </TableContainer>
        <Pagination total={total} size={size} onChange={handleChangePage} forceUpdatePage={page - 1} />
      </Paper>
    </Container>
  );
};
export default React.memo(HealthBenefitList);
